import { useState } from 'react';
import SEO from '@/components/shared/SEOHead';
import { useLanguage } from '@/providers/LanguageProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Use a direct URL instead of process.env
const API_URL = 'http://localhost:5001/api';

const ClientAccess = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/client/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to verify email');
        toast.error(t('clientAccess.accessDenied') || 'Access Denied', {
          description: data.message || t('clientAccess.emailNotRegistered') || 'Email not registered in our system',
        });
        setIsLoading(false);
        return;
      }

      // Success - email is verified
      toast.success(t('clientAccess.accessGranted') || 'Access Granted', {
        description: t('clientAccess.welcomeBack') || 'Welcome back!',
      });

      // Store client info in session storage
      sessionStorage.setItem('clientId', data.clientId);
      sessionStorage.setItem('customerName', data.customerName);
      sessionStorage.setItem('clientEmail', email);
      
      // Redirect to client dashboard or projects page
      setTimeout(() => {
        navigate('/client/dashboard'); // You'll need to create this route
      }, 1000);
      
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred. Please try again.');
      toast.error(t('clientAccess.error') || 'Error', {
        description: t('clientAccess.unexpectedError') || 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title={t('clientAccess.title')}
        description={t('clientAccess.description')}
        keywords="client access, client portal, login"
      />
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="border-2">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">{t('clientAccess.title') || 'Client Access'}</CardTitle>
              <CardDescription>
                {t('clientAccess.loginPrompt') || 'Enter your registered email to access your projects'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    {t('clientAccess.emailLabel') || 'Registered Email'}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full"
                  />
                  {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('clientAccess.loggingIn') || 'Logging in...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('clientAccess.login') || 'Login'}
                    </span>
                  )}
                </Button>
              </form>
              <div className="mt-4 text-center text-sm text-gray-500">
                {t('clientAccess.needHelp') || 'Need help?'}{' '}
                <a href="/contact" className="text-primary hover:underline">
                  {t('clientAccess.contactUs') || 'Contact us'}
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default ClientAccess;